/**
 * TSM Items Report Renderer
 *
 * Multi-location TSM leaderboard ranking Service Advisors on their **penetration %**
 * of 7 tracked labor-op categories (Batteries, Brake Repairs, Brake Services,
 * Cabin Air Filters, Engine Air Filters, Alignments, Wiper Blades).
 *
 * Bucketed into 4 groups (Domestic / Import / Express / Unassigned); each group
 * is ranked independently. Per-metric ranks sum to a Total Score; lowest wins.
 *
 * Designed for printing landscape with one group per page for a bulletin board.
 */

import React, { useMemo, useState } from 'react';
import {
  Squares2X2Icon,
  FlagIcon,
  BoltIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { SortableHeader } from './TsmRankingShared';

export type ItemKey =
  | 'battery'
  | 'brakeRepair'
  | 'brakeService'
  | 'cabinFilter'
  | 'engineFilter'
  | 'alignment'
  | 'wiper';

export type TsmItemsGroupKey = 'Domestic' | 'Import' | 'Express' | 'Unassigned';

export interface TsmItemsRow {
  advisorId: string;
  advisorName: string;
  locationId: number;
  locationName: string;
  totalROs: number;
  itemCounts: Record<ItemKey, number>;
  itemPcts: Record<ItemKey, number>;
  itemRanks: Record<ItemKey, number>;
  totalItemsSold: number;
  totalScore: number;
}

export interface TsmItemsMetricLeader {
  metric: ItemKey;
  label: string;
  winners: Array<{
    advisorId: string;
    advisorName: string;
    locationName: string;
    count: number;
    pct: number;
  }>;
}

export interface TsmItemsLocationRow {
  locationId: number;
  locationName: string;
  advisorCount: number;
  totalROs: number;
  itemCounts: Record<ItemKey, number>;
  itemPcts: Record<ItemKey, number>;
}

export interface TsmItemsMultiLocationExcluded {
  advisorId: string;
  advisorName: string;
  locations: string[];
}

export interface TsmItemsGroupSection {
  group: TsmItemsGroupKey;
  totalAdvisors: number;
  podium: TsmItemsRow[];
  metricLeaders: TsmItemsMetricLeader[];
  rows: TsmItemsRow[];
}

export interface TsmItemsReportData {
  reportMonth: { year: number; month: number; monthName: string };
  asOf: string | null;
  totalAdvisors: number;
  itemLabels: Record<ItemKey, string>;
  itemKeys: ItemKey[];
  nadaBenchmarks: Record<ItemKey, number | null>;
  groups: {
    domestic: TsmItemsGroupSection;
    import: TsmItemsGroupSection;
    express: TsmItemsGroupSection;
    unassigned: TsmItemsGroupSection;
  };
  multiLocationExcluded?: TsmItemsMultiLocationExcluded[];
  locationRows?: TsmItemsLocationRow[];
}

const COMPACT_LABELS: Record<ItemKey, string> = {
  battery: 'Batt',
  brakeRepair: 'Brake Rep',
  brakeService: 'Brake Svc',
  cabinFilter: 'Cabin Air',
  engineFilter: 'Eng Air',
  alignment: 'Align',
  wiper: 'Wiper',
};

function fmtAsOf(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ──────────────────────────────────────────────────────────────────────────
// NADA Benchmark legend strip
// ──────────────────────────────────────────────────────────────────────────

const NadaBenchmarkLegend: React.FC<{
  itemKeys: ItemKey[];
  benchmarks: Record<ItemKey, number | null>;
}> = ({ itemKeys, benchmarks }) => (
  <div className="mb-6 print:mb-4 print:break-inside-avoid">
    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
      NADA Benchmarks{' '}
      <span className="text-gray-400 normal-case font-normal italic">
        (industry-standard penetration % targets)
      </span>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 print:grid-cols-7">
      {itemKeys.map((k) => {
        const target = benchmarks[k];
        return (
          <div
            key={k}
            className="bg-blue-50 border border-blue-200 rounded-md p-2 text-center"
          >
            <div className="text-[10px] uppercase tracking-wide text-blue-600 mb-1 leading-tight">
              {COMPACT_LABELS[k]}
            </div>
            <div className="text-sm font-bold text-blue-900 tabular-nums">
              {target === null ? '—' : `${target.toFixed(1)}%`}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────────────────
// Per-group metric leaders strip
// ──────────────────────────────────────────────────────────────────────────

const MetricLeaderCards: React.FC<{
  leaders: TsmItemsMetricLeader[];
}> = ({ leaders }) => (
  <div className="mb-6 print:mb-4">
    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
      Metric Leaders
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 print:grid-cols-7">
      {leaders.map((l) => (
        <div
          key={l.metric}
          className="bg-white border border-gray-200 rounded-md p-2 text-center"
        >
          <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1 leading-tight">
            {l.label}
          </div>
          {l.winners.length === 0 ? (
            <div className="text-xs text-gray-400 italic">—</div>
          ) : (
            <>
              <div className="text-sm font-semibold text-gray-900 leading-tight">
                {l.winners.map((w) => w.advisorName).join(', ')}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                {l.winners[0].count} ({l.winners[0].pct.toFixed(1)}%)
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────────────────
// Sortable ranked table (per group)
// ──────────────────────────────────────────────────────────────────────────

type SortKey =
  | 'rank'
  | 'advisorName'
  | 'locationName'
  | 'totalROs'
  | 'totalScore'
  | ItemKey;

const SORT_DEFAULT_DIR: Record<SortKey, 'asc' | 'desc'> = {
  rank: 'asc',
  advisorName: 'asc',
  locationName: 'asc',
  totalROs: 'desc',
  totalScore: 'asc',
  battery: 'desc',
  brakeRepair: 'desc',
  brakeService: 'desc',
  cabinFilter: 'desc',
  engineFilter: 'desc',
  alignment: 'desc',
  wiper: 'desc',
};

function rankColor(rank: number, total: number): string {
  const q1 = Math.max(1, Math.ceil(total * 0.25));
  const q4 = Math.floor(total * 0.75);
  if (rank <= q1) return 'bg-emerald-100 text-emerald-800';
  if (rank > q4) return 'bg-rose-100 text-rose-800';
  return 'bg-gray-100 text-gray-700';
}

const RankedTable: React.FC<{
  rows: TsmItemsRow[];
  itemKeys: ItemKey[];
}> = ({ rows, itemKeys }) => {
  // Backend already sorted by totalScore ASC — that produces the canonical
  // rank-by-position. Capture it once before any user resorts.
  const baseOrderRank = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((r, i) => map.set(`${r.advisorId}_${r.locationId}`, i + 1));
    return map;
  }, [rows]);

  const [sortKey, setSortKey] = useState<SortKey>('rank');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const onClickSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(SORT_DEFAULT_DIR[key]);
    }
  };

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      switch (sortKey) {
        case 'rank':
          av = baseOrderRank.get(`${a.advisorId}_${a.locationId}`) ?? 0;
          bv = baseOrderRank.get(`${b.advisorId}_${b.locationId}`) ?? 0;
          break;
        case 'advisorName':
          av = a.advisorName;
          bv = b.advisorName;
          break;
        case 'locationName':
          av = a.locationName;
          bv = b.locationName;
          break;
        case 'totalROs':
          av = a.totalROs;
          bv = b.totalROs;
          break;
        case 'totalScore':
          av = a.totalScore;
          bv = b.totalScore;
          break;
        default:
          // Item key — sort on penetration %.
          av = a.itemPcts[sortKey];
          bv = b.itemPcts[sortKey];
      }
      if (typeof av === 'string' && typeof bv === 'string') {
        return av.localeCompare(bv) * dir;
      }
      return ((av as number) - (bv as number)) * dir;
    });
    return arr;
  }, [rows, sortKey, sortDir, baseOrderRank]);

  const total = rows.length;
  return (
    <div className="overflow-x-auto print:overflow-visible">
      <table className="min-w-full text-xs print:text-[10px]">
        <thead>
          <tr className="bg-gray-50 border-b-2 border-gray-300">
            <SortableHeader<SortKey>
              label="#"
              sortKey="rank"
              activeKey={sortKey}
              activeDir={sortDir}
              onClick={onClickSort}
              align="right"
            />
            <SortableHeader<SortKey>
              label="Advisor"
              sortKey="advisorName"
              activeKey={sortKey}
              activeDir={sortDir}
              onClick={onClickSort}
            />
            <SortableHeader<SortKey>
              label="Location"
              sortKey="locationName"
              activeKey={sortKey}
              activeDir={sortDir}
              onClick={onClickSort}
            />
            <SortableHeader<SortKey>
              label="ROs"
              sortKey="totalROs"
              activeKey={sortKey}
              activeDir={sortDir}
              onClick={onClickSort}
              align="right"
            />
            {itemKeys.map((k) => (
              <SortableHeader<SortKey>
                key={k}
                label={COMPACT_LABELS[k]}
                sortKey={k}
                activeKey={sortKey}
                activeDir={sortDir}
                onClick={onClickSort}
                align="right"
              />
            ))}
            <SortableHeader<SortKey>
              label="Score"
              sortKey="totalScore"
              activeKey={sortKey}
              activeDir={sortDir}
              onClick={onClickSort}
              align="right"
              emphasize
            />
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r, i) => {
            const baseRank = baseOrderRank.get(`${r.advisorId}_${r.locationId}`) ?? i + 1;
            return (
              <tr
                key={r.advisorId + r.locationId}
                className={i % 2 === 1 ? 'bg-gray-50' : 'bg-white'}
              >
                <td className="px-2 py-1.5 text-right font-bold text-gray-900">
                  {baseRank}
                </td>
                <td className="px-2 py-1.5 text-left text-gray-900">
                  {r.advisorName}
                </td>
                <td className="px-2 py-1.5 text-left text-gray-600">
                  {r.locationName}
                </td>
                <td className="px-2 py-1.5 text-right text-gray-700">
                  {r.totalROs}
                </td>
                {itemKeys.map((k) => (
                  <td
                    key={k}
                    className={`px-2 py-1.5 text-right font-semibold ${rankColor(r.itemRanks[k], total)}`}
                  >
                    {r.itemCounts[k]}{' '}
                    <span className="text-[10px] font-normal opacity-80">
                      ({r.itemPcts[k].toFixed(1)}%)
                    </span>
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right font-bold text-gray-900 bg-gray-50">
                  {r.totalScore}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Per-location aggregate (cross-group)
// ──────────────────────────────────────────────────────────────────────────

const LocationTable: React.FC<{
  locationRows: TsmItemsLocationRow[];
  tsmRowsByLocation: Map<number, TsmItemsRow[]>;
  itemKeys: ItemKey[];
}> = ({ locationRows, tsmRowsByLocation, itemKeys }) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggle = (locationId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(locationId)) next.delete(locationId);
      else next.add(locationId);
      return next;
    });
  };

  return (
    <div className="overflow-x-auto print:overflow-visible">
      <table className="min-w-full text-xs print:text-[10px]">
        <thead>
          <tr className="bg-gray-700 text-white">
            <th className="px-2 py-2 text-left">Location</th>
            <th className="px-2 py-2 text-right">Advisors</th>
            <th className="px-2 py-2 text-right">ROs</th>
            {itemKeys.map((k) => (
              <th key={k} className="px-2 py-2 text-right">
                {COMPACT_LABELS[k]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {locationRows.map((lr) => {
            const tsmRows = tsmRowsByLocation.get(lr.locationId) ?? [];
            const isExpanded = expanded.has(lr.locationId);
            return (
              <React.Fragment key={lr.locationId}>
                <tr
                  className="bg-white hover:bg-gray-50 cursor-pointer print:cursor-default"
                  onClick={() => toggle(lr.locationId)}
                >
                  <td className="px-2 py-1.5 text-left font-medium text-gray-900">
                    {isExpanded ? '▾ ' : '▸ '}
                    {lr.locationName}
                  </td>
                  <td className="px-2 py-1.5 text-right text-gray-700">
                    {lr.advisorCount}
                  </td>
                  <td className="px-2 py-1.5 text-right text-gray-700">
                    {lr.totalROs}
                  </td>
                  {itemKeys.map((k) => (
                    <td key={k} className="px-2 py-1.5 text-right text-gray-700">
                      {lr.itemCounts[k]}{' '}
                      <span className="text-[10px] text-gray-500">
                        ({lr.itemPcts[k].toFixed(1)}%)
                      </span>
                    </td>
                  ))}
                </tr>
                {isExpanded && tsmRows.length > 0 && (
                  <tr className="bg-gray-50">
                    <td colSpan={3 + itemKeys.length} className="px-4 py-2">
                      <div className="text-[10px] text-gray-600 mb-1">
                        TSMs ranked at this location:
                      </div>
                      <ol className="text-xs text-gray-700 list-decimal list-inside space-y-0.5">
                        {tsmRows.map((tr) => (
                          <li key={tr.advisorId}>
                            {tr.advisorName}{' '}
                            <span className="text-gray-500">
                              (Score {tr.totalScore}, {tr.totalItemsSold} items
                              sold across {tr.totalROs} ROs)
                            </span>
                          </li>
                        ))}
                      </ol>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Group section view (one per Domestic / Import / Express / Unassigned)
// ──────────────────────────────────────────────────────────────────────────

const GROUP_VISUALS: Record<TsmItemsGroupKey, {
  label: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  badgeClass: string;
  headingIconBg: string;
  headingIconText: string;
}> = {
  Domestic: {
    label: 'Domestic',
    Icon: FlagIcon,
    badgeClass: 'bg-blue-50 text-blue-800 ring-1 ring-blue-200',
    headingIconBg: 'bg-blue-100',
    headingIconText: 'text-blue-700',
  },
  Import: {
    label: 'Import',
    Icon: Squares2X2Icon,
    badgeClass: 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200',
    headingIconBg: 'bg-emerald-100',
    headingIconText: 'text-emerald-700',
  },
  Express: {
    label: 'Express',
    Icon: BoltIcon,
    badgeClass: 'bg-orange-50 text-orange-800 ring-1 ring-orange-200',
    headingIconBg: 'bg-orange-100',
    headingIconText: 'text-orange-700',
  },
  Unassigned: {
    label: 'Unassigned',
    Icon: QuestionMarkCircleIcon,
    badgeClass: 'bg-gray-100 text-gray-700 ring-1 ring-gray-200',
    headingIconBg: 'bg-gray-100',
    headingIconText: 'text-gray-600',
  },
};

const GroupSectionView: React.FC<{
  section: TsmItemsGroupSection;
  itemKeys: ItemKey[];
  pageBreakBefore: boolean;
}> = ({ section, itemKeys, pageBreakBefore }) => {
  const visuals = GROUP_VISUALS[section.group];
  const Icon = visuals.Icon;
  const isUnassigned = section.group === 'Unassigned';

  return (
    <section className={`mb-10 ${pageBreakBefore ? 'print:break-before-page' : ''}`}>
      <div className="flex items-center justify-between mb-3 print:mb-2">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${visuals.headingIconBg}`}>
            <Icon className={`h-5 w-5 ${visuals.headingIconText}`} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 print:text-lg">
              {visuals.label}
              <span
                className={`ml-3 text-xs font-medium align-middle px-2 py-0.5 rounded ${visuals.badgeClass}`}
              >
                {section.totalAdvisors}{' '}
                {section.totalAdvisors === 1 ? 'advisor' : 'advisors'}
              </span>
            </h2>
            {isUnassigned && section.totalAdvisors > 0 && (
              <p className="text-xs text-amber-700 mt-0.5 italic">
                These advisors don't have a group assigned yet. Visit{' '}
                <span className="font-semibold">Configuration → TSM Groups</span>{' '}
                to assign them.
              </p>
            )}
          </div>
        </div>
      </div>

      {section.totalAdvisors === 0 ? (
        <div className="text-sm text-gray-500 italic px-3 py-6 bg-gray-50 border border-gray-100 rounded">
          No advisors{' '}
          {section.group === 'Unassigned'
            ? 'are currently unassigned (all eligible TSMs have a group set).'
            : `assigned to the ${visuals.label} group yet.`}
        </div>
      ) : (
        <>
          <MetricLeaderCards leaders={section.metricLeaders} />
          <RankedTable rows={section.rows} itemKeys={itemKeys} />
        </>
      )}
    </section>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Top-level report
// ──────────────────────────────────────────────────────────────────────────

export const TsmItemsReport: React.FC<{ data: TsmItemsReportData }> = ({ data }) => {
  const tsmRowsByLocation = useMemo(() => {
    const map = new Map<number, TsmItemsRow[]>();
    if (data?.groups) {
      const allRows = [
        ...data.groups.domestic.rows,
        ...data.groups.import.rows,
        ...data.groups.express.rows,
        ...data.groups.unassigned.rows,
      ];
      for (const r of allRows) {
        const list = map.get(r.locationId) ?? [];
        list.push(r);
        map.set(r.locationId, list);
      }
      map.forEach((list) => {
        list.sort((a, b) => a.totalScore - b.totalScore);
      });
    }
    return map;
  }, [data?.groups]);

  if (!data) {
    return (
      <div className="text-center py-12 text-gray-500">
        No data available for this period.
      </div>
    );
  }

  if (data.totalAdvisors === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No Service Advisor activity found for{' '}
        {data.reportMonth?.monthName ?? 'this period'}.
      </div>
    );
  }

  const itemKeys = data.itemKeys;

  return (
    <div className="text-gray-900">
      {/* Header */}
      <div className="mb-6 print:mb-4 print:break-inside-avoid">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 print:text-xl">
              TSM Items Report
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {data.reportMonth.monthName} {data.reportMonth.year} ·{' '}
              {data.totalAdvisors} Service Advisors split into Domestic / Import
              / Express on 7 labor-op penetration metrics
            </p>
          </div>
          <div className="text-xs text-gray-500">
            Snapshot as of {fmtAsOf(data.asOf)}
          </div>
        </div>
      </div>

      <NadaBenchmarkLegend
        itemKeys={itemKeys}
        benchmarks={data.nadaBenchmarks}
      />

      {/* Four group sections — each on its own printed page */}
      <GroupSectionView
        section={data.groups.domestic}
        itemKeys={itemKeys}
        pageBreakBefore={false}
      />
      <GroupSectionView
        section={data.groups.import}
        itemKeys={itemKeys}
        pageBreakBefore
      />
      <GroupSectionView
        section={data.groups.express}
        itemKeys={itemKeys}
        pageBreakBefore
      />
      <GroupSectionView
        section={data.groups.unassigned}
        itemKeys={itemKeys}
        pageBreakBefore
      />

      {data.locationRows && data.locationRows.length > 0 && (
        <div className="mt-10 print:mt-8 print:break-before-page">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            By Location{' '}
            <span className="text-gray-400 normal-case font-normal">
              (click a row to expand its TSMs)
            </span>
          </div>
          <LocationTable
            locationRows={data.locationRows}
            tsmRowsByLocation={tsmRowsByLocation}
            itemKeys={itemKeys}
          />
        </div>
      )}

      {data.multiLocationExcluded && data.multiLocationExcluded.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500 leading-relaxed print:break-inside-avoid">
          <strong className="text-gray-700">
            Excluded — multi-location fill-in advisors:
          </strong>{' '}
          {data.multiLocationExcluded
            .map((a) => `${a.advisorName} (covered ${a.locations.join(', ')})`)
            .join('; ')}
          .{' '}
          <span className="italic">
            These TSMs wrote ROs at 2+ locations this month — typically
            covering vacations across the company — so they're removed from
            the head-to-head rankings.
          </span>
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500 leading-relaxed print:break-inside-avoid">
        <strong className="text-gray-700">How scoring works:</strong> Each
        group (Domestic / Import / Express / Unassigned) is ranked independently
        — a TSM ranked #1 in Domestic is "best Domestic TSM", not necessarily
        best company-wide. Within each group, every advisor is ranked 1..N on
        each of the 7 metrics. Standard competition ranking — ties share the
        lower rank (1, 2, 2, 4). The Total Score sums all 7 within-group ranks.{' '}
        <em>Lower score wins.</em>{' '}
        <strong className="text-gray-700">Penetration %:</strong> (ROs the
        advisor wrote with at least one CP/SC labor line matching the item's
        jobcode pattern) ÷ (all ROs they wrote in the month, any pay-type).
        Column headers are clickable to re-sort the table.{' '}
        <strong className="text-gray-700">Tracked labor-op patterns:</strong>{' '}
        Batteries (BAT, BATTERY*, RBATT — excludes BATTFOB, BG*, BATSVC, DIAGBATT),
        Brake Repairs (BRAKER*, BRAKEF*, BRAKESPCL*, BRAKEAFTMKT*), Brake
        Services (BRAKESVC*), Cabin Air Filters (FILTCAF*, CVCFILTCAF*),
        Engine Air Filters (FILTAF*, CVCFILTAF*), Alignments (ALIGN* —
        excludes ALGNCHK alignment-check-only codes), Wiper Blades (WIPER*,
        CVCWIPER*). Per-location brand suffixes (e.g. ALIGNK at Loc 14 Kia,
        FILTCAFH at Loc 2 Honda) are auto-matched by the pattern.{' '}
        <strong className="text-gray-700">Eligibility:</strong> To qualify, an
        advisor must have written at least 1 RO per Service working day of the
        month at their location — e.g. on working day 16 they need ≥ 16 ROs.
        Loc 5 &amp; Loc 11 are Service-closed on Saturdays so their threshold is
        lower than the other six. Locations 12 &amp; 44 are Body Shop and excluded.
        Group assignments are managed in{' '}
        <span className="font-semibold">Configuration → TSM Groups</span>.
      </div>
    </div>
  );
};
